import { useState, useEffect, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { X, Download, Trash2, Loader2, Check, AlertTriangle, Clock, Film } from "lucide-react";
import { listReelRenders, deleteReelRender, getReelRenderDownloadUrl } from "@/services/editService";

const fmtBytes = (b) => (!b ? "" : b >= 1e6 ? `${(b / 1e6).toFixed(1)} MB` : `${Math.round(b / 1e3)} KB`);
const fmtDur = (s) => (!s ? "" : `${Math.floor(s / 60)}:${String(Math.round(s % 60)).padStart(2, "0")}`);
function fmtWhen(iso) {
  if (!iso) return "";
  const d = new Date(iso), diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" }) + " " +
         d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
}

const STATUS = {
  done: { Icon: Check, color: "text-emerald-400", label: "Ready" },
  rendering: { Icon: Loader2, color: "text-[var(--color-gold-400)]", label: "Rendering", spin: true },
  queued: { Icon: Clock, color: "text-white/50", label: "Queued" },
  failed: { Icon: AlertTriangle, color: "text-red-400", label: "Failed" },
};

/** Slide-over listing this project's MP4 exports: download / delete / live status. */
export default function ExportsPanel({ open, onClose, projectId, refreshKey }) {
  const [renders, setRenders] = useState(null);
  const pollRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const list = await listReelRenders(projectId);
      setRenders(list);
      return list;
    } catch (e) {
      setRenders([]);
      return [];
    }
  }, [projectId]);

  // (re)load on open / after a new export; poll while anything is in progress
  useEffect(() => {
    if (!open) { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } return; }
    let cancelled = false;
    const tick = async () => {
      const list = await load();
      if (cancelled) return;
      const active = list.some((r) => r.status === "queued" || r.status === "rendering");
      if (active && !pollRef.current) {
        pollRef.current = setInterval(load, 2500);
      } else if (!active && pollRef.current) {
        clearInterval(pollRef.current); pollRef.current = null;
      }
    };
    tick();
    return () => { cancelled = true; if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
  }, [open, refreshKey, load]);

  const download = (jobId) => {
    const a = document.createElement("a");
    a.href = getReelRenderDownloadUrl(jobId); a.rel = "noopener";
    document.body.appendChild(a); a.click(); a.remove();
  };

  const remove = async (jobId) => {
    setRenders((prev) => prev?.filter((r) => r.id !== jobId));
    try { await deleteReelRender(jobId); }
    catch (e) { toast.error("Could not delete export"); load(); }
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div className="absolute inset-0 z-30 bg-black/50" onClick={onClose}
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} />
          <motion.div className="absolute top-0 right-0 z-40 h-full w-[min(92vw,420px)] bg-[#15130f] border-l border-white/10 flex flex-col shadow-2xl"
            initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }} transition={{ type: "tween", duration: 0.2 }}>
            <div className="flex items-center gap-2 px-4 py-3 border-b border-white/10">
              <Film size={16} className="text-[var(--color-gold-400)]" />
              <h3 className="text-sm font-semibold text-white flex-1">Exports</h3>
              <button onClick={onClose} className="p-1.5 text-white/60 hover:text-white"><X size={18} /></button>
            </div>

            <div className="flex-1 overflow-y-auto p-3 space-y-2">
              {renders === null ? (
                <div className="flex justify-center py-10"><Loader2 className="animate-spin text-white/40" size={22} /></div>
              ) : renders.length === 0 ? (
                <div className="text-center py-12 px-4">
                  <Film size={28} className="text-white/20 mx-auto mb-3" />
                  <p className="text-[12px] text-white/50">No exports yet.</p>
                  <p className="text-[11px] text-white/30 mt-1">Click <span className="text-[var(--color-gold-400)]">Export MP4</span> to render your reel.</p>
                </div>
              ) : renders.map((r) => {
                const st = STATUS[r.status] || STATUS.queued;
                return (
                  <div key={r.id} className="rounded-lg border border-white/10 bg-white/5 p-3">
                    <div className="flex items-center gap-2">
                      <st.Icon size={14} className={`${st.color} ${st.spin ? "animate-spin" : ""}`} />
                      <span className={`text-[11px] font-medium ${st.color}`}>{st.label}</span>
                      <span className="text-[10px] text-white/40 ml-auto">{fmtWhen(r.created_at)}</span>
                      <button onClick={() => remove(r.id)} className="text-white/30 hover:text-red-400 ml-1" title="Delete export"><Trash2 size={13} /></button>
                    </div>
                    {r.status === "done" && (
                      <div className="flex items-center gap-2 mt-2">
                        <span className="text-[11px] text-white/50 font-mono">{fmtDur(r.seconds)} · {fmtBytes(r.bytes)}</span>
                        <button onClick={() => download(r.id)}
                          className="ml-auto flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-[rgba(212,168,83,0.15)] border border-[rgba(212,168,83,0.5)] text-[var(--color-gold-300)] text-[11px] hover:bg-[rgba(212,168,83,0.25)]">
                          <Download size={12} /> Download
                        </button>
                      </div>
                    )}
                    {r.status === "failed" && r.error && (
                      <p className="text-[10px] text-red-300/70 mt-1.5 line-clamp-2 break-words">{r.error}</p>
                    )}
                  </div>
                );
              })}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
